import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import {
  COACHING_RECORDING_CONFIG,
  COACHING_INTERRUPTION_CONFIG,
} from '../utils/coaching-constants';
import { tlLogger } from '@taleemabad/shared';
import { RecordingService } from '../plugins/RecordingServicePlugin';
import { createAudioMergeWorker } from '../workers/audio-merge-worker-factory';

export enum AudioRecorderStatus {
  IDLE = 'idle',
  RECORDING = 'recording',
  PAUSED = 'paused',
  STOPPED = 'stopped',
}

export interface AudioRecordingStitchConfig {
  blob: Blob;
  duration: number;
}

export enum AudioRecordingErrorType {
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  MICROPHONE_BUSY = 'MICROPHONE_BUSY',
  INITIALIZATION_FAILED = 'INITIALIZATION_FAILED',
  UNSUPPORTED_DEVICE = 'UNSUPPORTED_DEVICE',
  HARDWARE_FAILURE = 'HARDWARE_FAILURE',
}

export class AudioRecordingError extends Error {
  constructor(
    public type: AudioRecordingErrorType,
    message: string,
  ) {
    super(message);
    this.name = 'AudioRecordingError';
  }
}

interface AudioRecorderOptions {
  onStop?: (blob: Blob, duration: number) => void;
  onAutoSave?: (blob: Blob, duration: number) => any | Promise<any>;
  onAutoSaveChunk?: (chunkBlob: Blob, duration: number) => any | Promise<any>;
  maxDurationMs?: number;
}

// ─── Web Worker–based audio merge ─────────────────────────────────────────
//
// The heavy decode → render → encodeWAV pipeline now runs entirely inside a
// Web Worker so it never blocks the main thread or inflates the main JS heap.
// We keep the worker alive across merges (singleton) and fall back to the
// main thread gracefully when Workers aren't available (e.g. in tests).
// ──────────────────────────────────────────────────────────────────────────

let mergeWorker: Worker | null = null;
let workerUnavailable = false;

/**
 * Returns the singleton merge worker, creating it on first call.
 * Returns `null` if Web Workers are not available (e.g. in test/SSR).
 *
 * NOTE: The actual Worker constructor lives in audio-merge-worker-factory.ts
 * because `import.meta.url` is a PARSE-TIME syntax error in Jest's CJS
 * transform — isolating it in a separate module lets tests mock it away.
 */
function getMergeWorker(): Worker | null {
  if (workerUnavailable) return null;
  if (mergeWorker) return mergeWorker;

  try {
    mergeWorker = createAudioMergeWorker();
    if (!mergeWorker) {
      workerUnavailable = true;
      return null;
    }
    // If the worker fatally errors, mark it unavailable so we fall back
    mergeWorker.onerror = () => {
      mergeWorker?.terminate();
      mergeWorker = null;
      workerUnavailable = true;
    };
    return mergeWorker;
  } catch {
    workerUnavailable = true;
    return null;
  }
}

/**
 * Sends two blobs to the Web Worker for off-thread merging.
 * Returns the merged WAV blob.
 */
function mergeAudioBlobsInWorker(oldBlob: Blob, newBlob: Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const worker = getMergeWorker();
    if (!worker) {
      reject(new Error('Worker unavailable'));
      return;
    }

    const onMessage = (e: MessageEvent) => {
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
      if (e.data.ok) {
        resolve(e.data.blob as Blob);
      } else {
        reject(new Error(e.data.error || 'Worker merge failed'));
      }
    };

    const onError = (err: ErrorEvent) => {
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
      reject(new Error(err.message || 'Worker error'));
    };

    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);
    worker.postMessage({ oldBlob, newBlob });
  });
}

/**
 * Main-thread fallback for environments where Web Workers are unavailable
 * (e.g. test runners, very old browsers).
 */
function encodeWAV(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const length = buffer.length;
  const bytesPerSample = 2; // 16-bit
  const dataSize = length * numChannels * bytesPerSample;
  const headerSize = 44;
  const arrayBuffer = new ArrayBuffer(headerSize + dataSize);
  const view = new DataView(arrayBuffer);

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++)
      view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
  view.setUint16(32, numChannels * bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = headerSize;
  for (let i = 0; i < length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, buffer.getChannelData(ch)[i]));
      view.setInt16(
        offset,
        sample < 0 ? sample * 0x8000 : sample * 0x7fff,
        true,
      );
      offset += 2;
    }
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

async function mergeAudioBlobsMainThread(
  oldBlob: Blob,
  newBlob: Blob,
): Promise<Blob> {
  const OAC =
    window.OfflineAudioContext || (window as any).webkitOfflineAudioContext;
  if (!OAC) throw new Error('OfflineAudioContext not supported');

  const decodeBlob = async (blob: Blob): Promise<AudioBuffer> => {
    const arrBuf = await blob.arrayBuffer();
    const tempCtx = new OAC(1, 1, 44100);
    return tempCtx.decodeAudioData(arrBuf);
  };

  const [oldBuf, newBuf] = await Promise.all([
    decodeBlob(oldBlob),
    decodeBlob(newBlob),
  ]);

  const channels = Math.max(oldBuf.numberOfChannels, newBuf.numberOfChannels);
  const sampleRate = oldBuf.sampleRate;
  const totalLength = oldBuf.length + newBuf.length;

  const offline = new OAC(channels, totalLength, sampleRate);

  const src1 = offline.createBufferSource();
  src1.buffer = oldBuf;
  src1.connect(offline.destination);
  src1.start(0);

  const src2 = offline.createBufferSource();
  src2.buffer = newBuf;
  src2.connect(offline.destination);
  src2.start(oldBuf.duration);

  const rendered = await offline.startRendering();
  return encodeWAV(rendered);
}

/**
 * Merges two audio blobs into a single valid WAV file.
 *
 * Strategy:
 *   1. Try Web Worker (off main thread, separate memory heap)
 *   2. Fall back to main-thread OfflineAudioContext if worker unavailable
 */
export async function mergeAudioBlobs(
  oldBlob: Blob,
  newBlob: Blob,
  _originalMimeType: string,
): Promise<Blob> {
  try {
    return await mergeAudioBlobsInWorker(oldBlob, newBlob);
  } catch {
    // Worker unavailable or failed — fall back to main thread
    tlLogger.log('AudioRecorder:WorkerFallback', {
      reason: 'Falling back to main-thread merge',
    });
    return mergeAudioBlobsMainThread(oldBlob, newBlob);
  }
}

/**
 * Custom hook for high-fidelity audio recording with smooth UI feedback.
 */
export const useAudioRecorder = (options?: AudioRecorderOptions) => {
  const [status, setStatus] = useState<AudioRecorderStatus>(
    AudioRecorderStatus.IDLE,
  );
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [mimeType, setMimeType] = useState('');

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const waveformIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const watchdogTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const stitchBlobRef = useRef<Blob | null>(null);
  const isMergingRef = useRef(false);
  const elapsedTimeRef = useRef(0);
  const contentReceivedRef = useRef(false);
  const recordingStartTimeRef = useRef(0); // wall-clock ms when current segment started
  const accumulatedTimeRef = useRef(0); // ms elapsed before current segment (pre-pause)
  const wakeLockRef = useRef<any>(null);
  const lastInterruptionTimeRef = useRef<number>(0);

  const getSupportedMimeType = useCallback(() => {
    const types = COACHING_RECORDING_CONFIG.SUPPORTED_MIME_TYPES;
    return types.find((type) => MediaRecorder.isTypeSupported(type)) || '';
  }, []);

  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const clearSafetyTimers = useCallback(() => {
    if (watchdogTimeoutRef.current) clearTimeout(watchdogTimeoutRef.current);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    if (waveformIntervalRef.current) clearInterval(waveformIntervalRef.current);
    if (autoSaveIntervalRef.current) clearInterval(autoSaveIntervalRef.current);
  }, []);

  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const requestWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        tlLogger.log('AudioRecorder:WakeLockAcquired', {});
        wakeLockRef.current.addEventListener('release', () => {
          tlLogger.log('AudioRecorder:WakeLockReleased', {});
        });
      }
    } catch (err) {
      tlLogger.error('AudioRecorder:WakeLockFailed', { error: String(err) });
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    try {
      if (wakeLockRef.current !== null) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    } catch (err) {
      tlLogger.error('AudioRecorder:WakeLockReleaseFailed', {
        error: String(err),
      });
    }
  }, []);

  /**
   * Starts the native foreground service on Android to keep microphone alive.
   * Requires POST_NOTIFICATIONS runtime permission and READ_PHONE_STATE manifest permission.
   * READ_PHONE_STATE must be declared in AndroidManifest.xml for TelephonyCallback to function.
   */
  const startNativeForegroundService = useCallback(async () => {
    try {
      if (Capacitor.getPlatform() === 'android') {
        // Android 13+ requires POST_NOTIFICATIONS runtime permission
        // for the foreground service notification to display
        const permStatus = await RecordingService.checkPermissions();
        if (permStatus.notifications !== 'granted') {
          const result = await RecordingService.requestPermissions();
          tlLogger.log('AudioRecorder:NotificationPermission', {
            status: result.notifications,
          });
        }

        await RecordingService.startForeground();
        tlLogger.log('AudioRecorder:ForegroundServiceStarted', {});
      }
    } catch (err) {
      tlLogger.error('AudioRecorder:ForegroundServiceStartFailed', {
        error: String(err),
      });
    }
  }, []);

  const stopNativeForegroundService = useCallback(async () => {
    try {
      if (Capacitor.getPlatform() === 'android') {
        await RecordingService.stopForeground();
        tlLogger.log('AudioRecorder:ForegroundServiceStopped', {});
      }
    } catch (err) {
      tlLogger.error('AudioRecorder:ForegroundServiceStopFailed', {
        error: String(err),
      });
    }
  }, []);

  const stopRecording = useCallback(() => {
    // ALWAYS aggressively clear safety locks, even if the MediaRecorder is inactive or null.
    // Otherwise, "Recording in progress" notifications get stuck indefinitely.
    clearSafetyTimers();
    releaseWakeLock();
    stopNativeForegroundService();

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      mediaRecorderRef.current.stop();
      setStatus(AudioRecorderStatus.IDLE);
      streamRef.current?.getTracks().forEach((track) => track.stop());
    }
  }, [clearSafetyTimers, releaseWakeLock, stopNativeForegroundService]);

  const startRecording = useCallback(
    async (stitchConfig?: AudioRecordingStitchConfig) => {
      try {
        clearSafetyTimers();
        // Keep stitch blob SEPARATE — never put it in chunksRef.
        // Binary-concatenating two audio containers creates an invalid file.
        stitchBlobRef.current = stitchConfig?.blob ?? null;
        chunksRef.current = [];
        contentReceivedRef.current = stitchConfig ? true : false;

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        streamRef.current = stream;

        // 🚨 PREVENT CORRUPTION: If resuming, prioritize the original blob's mimetype
        // if supported. Mixing mimetypes in a single blob via concatenation creates unplayable files.
        const suggestedType = stitchConfig?.blob?.type;
        const type =
          suggestedType && MediaRecorder.isTypeSupported(suggestedType)
            ? suggestedType
            : getSupportedMimeType();
        setMimeType(type);

        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: type,
          audioBitsPerSecond: COACHING_RECORDING_CONFIG.BIT_RATE,
        });

        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            const isFirstChunk = !contentReceivedRef.current;
            chunksRef.current.push(event.data);
            contentReceivedRef.current = true;

            if (isFirstChunk && optionsRef.current?.onAutoSave) {
              const firstBlob = new Blob([event.data], { type });
              optionsRef.current.onAutoSave(firstBlob, elapsedTimeRef.current);
            }

            if (watchdogTimeoutRef.current) {
              clearTimeout(watchdogTimeoutRef.current);
              watchdogTimeoutRef.current = null;
            }
          }
        };

        mediaRecorder.onstop = async () => {
          // Guard: skip if a previous merge/save is already in progress
          if (isMergingRef.current) {
            // If we are currently merging, retry stopping after a short delay
            setTimeout(
              () => mediaRecorder.onstop?.(new Event('stop') as any),
              500,
            );
            return;
          }
          isMergingRef.current = true;

          try {
            const newBlob = new Blob(chunksRef.current, { type });
            let finalBlob = newBlob;

            // Binary-concat stitch + new blob.
            // OfflineAudioContext decode is NOT used here — it OOMs on Android
            // WebView for recordings longer than ~29 min (decoded PCM ~600MB+).
            // Binary concat works reliably for WebM/M4A on Android/Chrome.
            // Cross-session stitching (resume) is handled by useAudioRecordingFlow.onStop.
            if (
              stitchBlobRef.current &&
              stitchBlobRef.current.size > 0 &&
              newBlob.size > 0
            ) {
              finalBlob = new Blob([stitchBlobRef.current, newBlob], {
                type: stitchBlobRef.current.type || type,
              });
              tlLogger.log('AudioRecorder:StitchConcatenated', {
                oldSize: stitchBlobRef.current.size,
                newSize: newBlob.size,
                finalSize: finalBlob.size,
              });
              stitchBlobRef.current = null;
            } else if (
              stitchBlobRef.current &&
              stitchBlobRef.current.size > 0
            ) {
              // No new data recorded — keep the old blob
              finalBlob = stitchBlobRef.current;
              stitchBlobRef.current = null;
            }

            // Update mimeType to match the actual blob format after merge
            if (finalBlob.type && finalBlob.type !== type) {
              setMimeType(finalBlob.type);
            }
            setAudioBlob(finalBlob);
            if (optionsRef.current?.onStop) {
              optionsRef.current.onStop(finalBlob, elapsedTimeRef.current);
            }
            tlLogger.log('AudioRecorder:Stopped', {
              size: finalBlob.size,
              duration: elapsedTimeRef.current,
            });
          } finally {
            isMergingRef.current = false;
          }
        };

        if (!audioContextRef.current) {
          const audioContext = new (window.AudioContext ||
            (window as any).webkitAudioContext)();
          audioContextRef.current = audioContext;

          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 64;
          analyserRef.current = analyser;
          audioContext.createMediaStreamSource(stream).connect(analyser);
        }

        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }

        await new Promise((resolve) => setTimeout(resolve, 50));

        mediaRecorder.start(COACHING_RECORDING_CONFIG.DATA_COLLECTION_SLICE_MS);

        watchdogTimeoutRef.current = setTimeout(() => {
          if (
            !contentReceivedRef.current &&
            statusRef.current === AudioRecorderStatus.RECORDING
          ) {
            tlLogger.error('AudioRecorder:WatchdogTriggered', {
              message: 'Hardware started but no data received',
            });
            stopRecording();
            setStatus(AudioRecorderStatus.IDLE);
          }
        }, 3000);

        setStatus(AudioRecorderStatus.RECORDING);

        if (stitchConfig) {
          setElapsedTime(stitchConfig.duration);
          elapsedTimeRef.current = stitchConfig.duration;
          accumulatedTimeRef.current = stitchConfig.duration;
        } else {
          setElapsedTime(0);
          elapsedTimeRef.current = 0;
          accumulatedTimeRef.current = 0;
        }

        recordingStartTimeRef.current = Date.now();

        await requestWakeLock();
        await startNativeForegroundService();
      } catch (err: unknown) {
        const isError = err instanceof Error;
        const errorMessage = isError ? err.message : 'Unknown error occurred';
        const errName = isError ? err.name : '';

        tlLogger.error('AudioRecorder:InitializationFailed', {
          error: errorMessage,
          errorName: errName || 'Unknown',
        });

        if (errName === 'NotReadableError' || errName === 'AbortError') {
          throw new AudioRecordingError(
            AudioRecordingErrorType.MICROPHONE_BUSY,
            'Microphone is busy.',
          );
        }

        if (
          errName === 'NotAllowedError' ||
          errName === 'PermissionDeniedError'
        ) {
          throw new AudioRecordingError(
            AudioRecordingErrorType.PERMISSION_DENIED,
            'Permission denied.',
          );
        }

        throw err;
      }
    },
    [
      getSupportedMimeType,
      clearSafetyTimers,
      stopRecording,
      requestWakeLock,
      startNativeForegroundService,
    ],
  );

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      accumulatedTimeRef.current += Date.now() - recordingStartTimeRef.current;
      mediaRecorderRef.current.pause();
      setStatus(AudioRecorderStatus.PAUSED);
    }
  }, []);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'paused') {
      recordingStartTimeRef.current = Date.now();
      mediaRecorderRef.current.resume();
      setStatus(AudioRecorderStatus.RECORDING);
    }
  }, []);

  const resetRecorder = useCallback(() => {
    stopRecording();
    setAudioBlob(null);
    setWaveformData([]);
    setElapsedTime(0);
    setMimeType('');
  }, [stopRecording]);

  useEffect(() => {
    const maxDurationMs =
      options?.maxDurationMs ?? COACHING_RECORDING_CONFIG.MAX_DURATION_MS;

    if (status === AudioRecorderStatus.RECORDING) {
      timerIntervalRef.current = setInterval(() => {
        // Use wall-clock time so browser tab throttling doesn't drift the display.
        const next =
          accumulatedTimeRef.current +
          (Date.now() - recordingStartTimeRef.current);
        const capped =
          maxDurationMs && next >= maxDurationMs ? maxDurationMs : next;
        setElapsedTime(capped);
        elapsedTimeRef.current = capped;
        if (maxDurationMs && next >= maxDurationMs) {
          stopRecording();
        }
      }, COACHING_RECORDING_CONFIG.TIMER_PRECISION_MS);

      waveformIntervalRef.current = setInterval(() => {
        if (analyserRef.current && status === AudioRecorderStatus.RECORDING) {
          const dataArray = new Uint8Array(
            analyserRef.current.frequencyBinCount,
          );
          analyserRef.current.getByteFrequencyData(dataArray);
          const normalizedData = Array.from(dataArray).map((v) => v / 255);
          setWaveformData(normalizedData);
        }
      }, COACHING_RECORDING_CONFIG.TIMER_PRECISION_MS);

      if (
        optionsRef.current?.onAutoSaveChunk ||
        optionsRef.current?.onAutoSave
      ) {
        autoSaveIntervalRef.current = setInterval(async () => {
          const currentOptions = optionsRef.current;

          // 🚨 CRITICAL: Force a flush of the current hardware buffer into memory.
          if (mediaRecorderRef.current?.state === 'recording') {
            try {
              mediaRecorderRef.current.requestData();
              await new Promise((resolve) => setTimeout(resolve, 200));
            } catch (e) {
              tlLogger.error('AudioRecorder:AutoSaveFlushFailed', { error: e });
            }
          }

          const hasNewContent = chunksRef.current.length > 0;

          if (hasNewContent && contentReceivedRef.current) {
            if (isMergingRef.current) return;
            isMergingRef.current = true;

            try {
              // Only save the NEW chunks since last auto-save (~120KB, not the entire recording)
              const chunkBlob = new Blob(chunksRef.current, { type: mimeType });
              chunksRef.current = [];

              if (currentOptions?.onAutoSaveChunk) {
                await currentOptions.onAutoSaveChunk(
                  chunkBlob,
                  elapsedTimeRef.current,
                );
              } else if (currentOptions?.onAutoSave) {
                // Legacy fallback: accumulate into stitchBlobRef for callers not using chunks
                stitchBlobRef.current = stitchBlobRef.current
                  ? new Blob([stitchBlobRef.current, chunkBlob], {
                      type: stitchBlobRef.current.type || mimeType,
                    })
                  : chunkBlob;
                await currentOptions.onAutoSave(
                  stitchBlobRef.current,
                  elapsedTimeRef.current,
                );
              }
            } finally {
              isMergingRef.current = false;
            }
          }
        }, COACHING_RECORDING_CONFIG.AUTO_SAVE_INTERVAL_MS);
      }
    }

    return () => clearSafetyTimers();
  }, [
    status,
    stopRecording,
    mimeType,
    clearSafetyTimers,
    options?.maxDurationMs,
  ]);

  // Strict unmount-only cleanup to prevent orphaned hardware locks
  useEffect(() => {
    return () => {
      releaseWakeLock();
      stopNativeForegroundService();
    };
  }, [releaseWakeLock, stopNativeForegroundService]);

  useEffect(() => {
    let listener: { remove: () => Promise<void> } | null = null;
    const isMobile = Capacitor.getPlatform() !== 'web';

    const handleInterruption = async () => {
      if (
        status === AudioRecorderStatus.RECORDING ||
        status === AudioRecorderStatus.PAUSED
      ) {
        tlLogger.log('AudioRecorder:Interrupted', { reason: 'Background' });

        // Immediately flush whatever chunks are already in memory to IndexedDB
        // BEFORE requestData(), in case the OS kills the process during the async wait.
        const currentOptions = optionsRef.current;
        if (
          chunksRef.current.length > 0 &&
          contentReceivedRef.current &&
          currentOptions?.onAutoSaveChunk
        ) {
          // Wait for any in-progress merge to complete before flushing (max 500ms).
          // Skipping when isMergingRef is true would silently drop chunks if the OS
          // kills the process during the async wait.
          let mergeWaitAttempts = 0;
          while (isMergingRef.current && mergeWaitAttempts < 10) {
            await new Promise((resolve) => setTimeout(resolve, 50));
            mergeWaitAttempts++;
          }
          try {
            const earlyBlob = new Blob(chunksRef.current, { type: mimeType });
            chunksRef.current = [];
            await currentOptions.onAutoSaveChunk(
              earlyBlob,
              elapsedTimeRef.current,
            );
          } catch {
            // Best-effort; continue to requestData path
          }
        }

        try {
          if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.requestData();
            // 🚨 CRITICAL: Give the browser a moment to flush hardware buffers into chunksRef.
            // requestData() is asynchronous; without this delay, chunksRef.current is often empty.
            await new Promise((resolve) => setTimeout(resolve, 150));
          }
        } catch (error: unknown) {
          // Permission revoked mid-recording (NotAllowedError on some browsers, SecurityError on others)
          const domErr = error as DOMException;
          if (
            domErr?.name === 'NotAllowedError' ||
            domErr?.name === 'SecurityError'
          ) {
            tlLogger.error('RecordingPermissionRevoked', {
              message: domErr.message,
            });
            stopRecording();
            setStatus(AudioRecorderStatus.STOPPED);
            return;
          }
          // Best-effort; continue for other transient errors
        }

        // Flush any remaining chunks that arrived from requestData().
        if (currentOptions?.onAutoSaveChunk || currentOptions?.onAutoSave) {
          // If an auto-save merge is in progress, wait for it to complete (max 500ms)
          // before flushing. Silently returning would drop the chunks permanently.
          if (isMergingRef.current) {
            const MAX_WAIT_MS = 500;
            const POLL_INTERVAL_MS = 50;
            let waited = 0;
            while (isMergingRef.current && waited < MAX_WAIT_MS) {
              await new Promise((resolve) =>
                setTimeout(resolve, POLL_INTERVAL_MS),
              );
              waited += POLL_INTERVAL_MS;
            }
          }
          isMergingRef.current = true;

          try {
            const hasNewContent = chunksRef.current.length > 0;

            if (hasNewContent && contentReceivedRef.current) {
              const chunkBlob = new Blob(chunksRef.current, { type: mimeType });
              chunksRef.current = [];

              if (currentOptions.onAutoSaveChunk) {
                await currentOptions.onAutoSaveChunk(
                  chunkBlob,
                  elapsedTimeRef.current,
                );
              } else if (currentOptions.onAutoSave) {
                stitchBlobRef.current = stitchBlobRef.current
                  ? new Blob([stitchBlobRef.current, chunkBlob], {
                      type: stitchBlobRef.current.type || mimeType,
                    })
                  : chunkBlob;
                await currentOptions.onAutoSave(
                  stitchBlobRef.current,
                  elapsedTimeRef.current,
                );
              }
            }
          } finally {
            isMergingRef.current = false;
          }
        }
      }
    };

    if (isMobile) {
      let recordingInterruptedListener: { remove: () => Promise<void> } | null =
        null;
      App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) {
          // Debounce: skip if flushed in last N milliseconds (app backgrounding can fire multiple times:
          // screen dim → notification → user action).
          const now = Date.now();
          if (
            now - lastInterruptionTimeRef.current >=
            COACHING_INTERRUPTION_CONFIG.APPSTATE_DEBOUNCE_DELAY_MS
          ) {
            lastInterruptionTimeRef.current = now;
            handleInterruption();
          }
        }
      }).then((l: PluginListenerHandle) => (listener = l));
      RecordingService.addListener(
        'recordingInterrupted',
        async (data: { reason: string }) => {
          tlLogger.log('RecordingInterrupted', { reason: data.reason });
          handleInterruption();
        },
      ).then((l: PluginListenerHandle) => (recordingInterruptedListener = l));
      return () => {
        recordingInterruptedListener?.remove();
        listener?.remove();
      };
    } else {
      const handleVisibility = () => {
        if (document.visibilityState === 'hidden') {
          handleInterruption();
        }
      };

      // Also catch page reloads/closes on web
      const handleBeforeUnload = () => {
        handleInterruption();
      };

      document.addEventListener('visibilitychange', handleVisibility);
      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibility);
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [status, mimeType]);

  return useMemo(
    () => ({
      status,
      audioBlob,
      waveformData,
      elapsedTime,
      mimeType,
      startRecording,
      pauseRecording,
      resumeRecording,
      stopRecording,
      resetRecorder,
    }),
    [
      status,
      audioBlob,
      waveformData,
      elapsedTime,
      mimeType,
      startRecording,
      pauseRecording,
      resumeRecording,
      stopRecording,
      resetRecorder,
    ],
  );
};
